import "../../styles/styles.css";
import DatePicker from "@rio-cloud/rio-uikit/DatePicker";
import TimePicker from "@rio-cloud/rio-uikit/TimePicker";
import Switch from "@rio-cloud/rio-uikit/Switch";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AutoSuggest, Button, Select, SelectOption } from "@rio-cloud/rio-uikit";
import { AutoSuggestSuggestion } from "@rio-cloud/rio-uikit/components/autosuggest/AutoSuggest";

import { NameInputChange } from "@components/Dialog/Receipt/form";
import { CoreContext } from "../../providers/CoreProvider";
import { useT } from "@hooks";
import { formatCurrency, normalizeText, parseCurrencyBRL } from "@utils/format";
import { isVehicleTagPaymentMethod } from "@utils/paymentMethodLabel";
import InputSelect, { InactiveSelectOption } from "@components/Dialog/Receipt/select";
import moment from "moment";
import { useIntl } from "react-intl";
import { AddressSuggestion, getAddressRouting } from "@services/form";
import { deriveStatusFromDueDate, hasInvalidOverdueDueDate } from "@utils/transactionStatus";
import { getRecurrencePeriodErrorKey, toYearAnchoredDate } from "@utils/transactionRecurrence";
import { isManualLikeSource, isMarketplaceTransaction } from "@utils/transactionSource";
import DueDateField from "@components/shared/DueDateField";
import RecurrenceFields, { buildRepeatEveryOptions, buildWeekDayOptions } from "@components/shared/RecurrenceFields";
import ComplementList, { Complement, criarComplementoId } from "@components/shared/ComplementList";
import { errorClass, renderErrorFeedback, renderErrorIcon } from "@components/shared/formFeedback";
import { useDriverSlots } from "@components/shared/useDriverSlots";
import { useDebouncedValue } from "@components/shared/useDebouncedValue";
import { useTripFiltersRequest } from "@components/shared/useTripFiltersRequest";
import { processAttachmentFileChange } from "@components/shared/attachmentFile";
import { buildEntityErrors } from "@components/shared/entityErrors";
import {
  buildTripFilters,
  resolveRioDriverIds,
  resolveTripSelection,
  tripMatchesFilters,
  TripDriverOption,
} from "@utils/rioTripOptions";
import { isMaintenanceCategoryName } from "@utils/maintenanceType";
import { MAINTENANCE_TYPES } from "@services/maintenance/types";
import type { RioTrip, RioTripsFilters } from "@types";

const MAX_DRIVERS = 3;

const TRIP_SEARCH_DELAY_MS = 400;

const EQUAL_COLUMNS_ROW_CLASS = "display-flex flex-wrap column-gap-15";

const EQUAL_COLUMN_CLASS = "flex-1-1-0 min-width-150";

const DRIVER_SLOT_IDS = ["driver-slot-1", "driver-slot-2", "driver-slot-3"];

const TRUCKER_PAY_TAG_LABEL = "trucker pay tag";

const ATTACHMENT_ICON_STYLE: React.CSSProperties = {
  flex: "0 0 auto",
  background: "none",
  border: "none",
  padding: 0,
  margin: 0,
  fontSize: "inherit",
};

const AXES_CATEGORY_OPTIONS: SelectOption[] = [
  { id: "2 eixos", label: "2 eixos" },
  { id: "3 eixos", label: "3 eixos" },
  { id: "4 eixos", label: "4 eixos" },
  { id: "5 eixos", label: "5 eixos" },
  { id: "6 eixos", label: "6 eixos" },
  { id: "7 eixos", label: "7 eixos" },
  { id: "8 eixos", label: "8 eixos" },
  { id: "9 eixos", label: "9 eixos" },
];

const clearFileInputValue = (inputRef: React.RefObject<HTMLInputElement | null>) => {
  if (inputRef.current) inputRef.current.value = "";
};

const isTruckerPayTagSupplier = (suppliers: SelectOption[], supplierId?: string): boolean => {
  if (!supplierId) return false;
  const label = suppliers.find((s) => String(s.id) === String(supplierId))?.label;
  return typeof label === "string" && label.trim().toLowerCase() === TRUCKER_PAY_TAG_LABEL;
};

const resolveMomentLocale = (locale: string): string => {
  if (locale.startsWith("en")) return "en-gb";
  if (locale.startsWith("es")) return "es";
  return "pt-br";
};

const getFuelUnitLabel = (subcategoryLabel: string): string => {
  if (subcategoryLabel === "gnv") return "M³";
  if (subcategoryLabel === "eletrico") return "kWh";
  return "L";
};

const getConsumptionUnitLabel = (subcategoryLabel: string): string => {
  if (subcategoryLabel === "gnv") return "m³";
  if (subcategoryLabel === "eletrico") return "kWh";
  return "L";
};

type TranslateFn = (id: string, values?: Record<string, string | number>) => string;

const calculateFuelUnitPrice = (totalValue: string, volume: string): string => {
  const cleanTotal = Number.parseFloat(totalValue.replace(/[^\d,]/g, "").replace(",", ".")) || 0;
  const cleanVolume = Number.parseFloat(volume.replace(/[^\d,]/g, "").replace(",", ".")) || 0;

  if (cleanVolume === 0) return "";

  const unitPrice = cleanTotal / cleanVolume;
  const truncated = Math.floor(unitPrice * 100) / 100;

  return truncated.toFixed(2).replace(".", ",");
};

type FormState = {
  date: string;
  value: string;
  branch: string;
  segment: string;
  supplier: string;
  trip: string;
  vehicle: string;
  driver: string[];
  category: string;
  subcategory: string;
  observation: string;
  maintenanceType?: string;
  startDate?: string;
  startTime?: string;
  stopLocation?: string;
  odometerKm?: string;
  volume?: string;
  fuelUnitPrice?: string;
  paymentMethod?: string;
  addOdometerPhoto?: boolean;
  invoiceGallery?: boolean;
  complements?: Complement[];
  firstFuelType?: string;
  firstFuelVolume?: string;
  firstFuelValue?: string;
  firstFuelUnitPrice?: string;
  secondFuelType?: string;
  secondFuelVolume?: string;
  secondFuelValue?: string;
  secondFuelUnitPrice?: string;
  totalVolume?: string;
  totalValue?: string;
  totalUnitPrice?: string;
  tankCapacity?: string;
  averageConsumption?: string;
  locationCoordinates?: string;
  hour?: string;
  tollLocation?: string;
  tollCoordinates?: string;
  tollLatitude?: string;
  tollLongitude?: string;
  concessionaire?: string;
  plaza?: string;
  tagType?: string;
  tagNumber?: string;
  axesCategory?: string;
  tollIsIntegration?: boolean;
  status?: string;
  isStatusManual?: boolean;
  dueDate?: string;
  isRecurring?: boolean;
  repeatEvery?: string;
  weekDays?: string[];
  recurrenceStart?: string;
  recurrenceEnd?: string;
  recurrenceInterval?: number;
  source?: string;
};

type FuelValueField = "value" | "firstFuelValue" | "secondFuelValue" | "totalValue";
type FuelVolumeField = "volume" | "firstFuelVolume" | "secondFuelVolume" | "totalVolume";
type FuelUnitPriceField = "fuelUnitPrice" | "firstFuelUnitPrice" | "secondFuelUnitPrice" | "totalUnitPrice";

type FuelPriceLink = {
  valueField: FuelValueField;
  volumeField: FuelVolumeField;
  unitPriceField: FuelUnitPriceField;
};

const FUEL_PRICE_LINKS: Record<string, FuelPriceLink> = {
  value: { valueField: "value", volumeField: "volume", unitPriceField: "fuelUnitPrice" },
  volume: { valueField: "value", volumeField: "volume", unitPriceField: "fuelUnitPrice" },
  firstFuelValue: {
    valueField: "firstFuelValue",
    volumeField: "firstFuelVolume",
    unitPriceField: "firstFuelUnitPrice",
  },
  firstFuelVolume: {
    valueField: "firstFuelValue",
    volumeField: "firstFuelVolume",
    unitPriceField: "firstFuelUnitPrice",
  },
  secondFuelValue: {
    valueField: "secondFuelValue",
    volumeField: "secondFuelVolume",
    unitPriceField: "secondFuelUnitPrice",
  },
  secondFuelVolume: {
    valueField: "secondFuelValue",
    volumeField: "secondFuelVolume",
    unitPriceField: "secondFuelUnitPrice",
  },
  totalValue: { valueField: "totalValue", volumeField: "totalVolume", unitPriceField: "totalUnitPrice" },
  totalVolume: { valueField: "totalValue", volumeField: "totalVolume", unitPriceField: "totalUnitPrice" },
};

const applyFuelPriceLink = (form: FormState, name: string, newValue: string): FormState => {
  const link = FUEL_PRICE_LINKS[name];
  if (!link) return form;

  const isValueField = name === link.valueField;
  const counterpart = isValueField ? form[link.volumeField] : form[link.valueField];
  if (!counterpart) return form;

  const unitPrice = isValueField
    ? calculateFuelUnitPrice(newValue, counterpart)
    : calculateFuelUnitPrice(counterpart, newValue);

  return { ...form, [link.unitPriceField]: unitPrice };
};

const withStatusFromDate = (form: FormState, referenceDate?: string): FormState => {
  const derived = deriveStatusFromDueDate(referenceDate);
  return derived && form.status !== derived ? { ...form, status: derived } : form;
};

const buildHybridFuelErrors = (form: FormState, t: TranslateFn): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!form.firstFuelType) errors.firstFuelType = t("expense.form.error.firstFuelType");
  if (!form.firstFuelVolume) errors.firstFuelVolume = t("expense.form.error.volume");
  if (!form.firstFuelValue) errors.firstFuelValue = t("expense.form.error.value");
  if (!form.secondFuelType) errors.secondFuelType = t("expense.form.error.secondFuelType");
  if (!form.secondFuelVolume) errors.secondFuelVolume = t("expense.form.error.volume");
  if (!form.secondFuelValue) errors.secondFuelValue = t("expense.form.error.value");
  if (!form.totalVolume) errors.totalVolume = t("expense.form.error.totalVolume");
  if (!form.totalValue) errors.totalValue = t("expense.form.error.totalValue");

  return errors;
};

const buildFuelErrors = (form: FormState, isHybrid: boolean, t: TranslateFn): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!form.startDate) errors.startDate = t("expense.form.error.startDate");
  if (!form.startTime) errors.startTime = t("expense.form.error.startTime");
  if (!form.stopLocation) errors.stopLocation = t("expense.form.error.stopLocation");
  if (!form.odometerKm) errors.odometerKm = t("expense.form.error.odometerKm");

  if (isHybrid) {
    Object.assign(errors, buildHybridFuelErrors(form, t));
    return errors;
  }

  if (!form.volume) errors.volume = t("expense.form.error.volume");

  if (!form.value) errors.value = t("expense.form.error.value");
  else if (parseCurrencyBRL(form.value) <= 0) errors.value = t("expense.form.error.valuePositive");

  return errors;
};

const buildComplementErrors = (complements: Complement[] | undefined, t: TranslateFn): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!complements || complements.length === 0) return errors;

  complements.forEach((complement, index) => {
    if (!complement.type) errors[`${index}_type`] = t("common.placeholder.select");
    if (!complement.volume) errors[`${index}_volume`] = t("expense.form.error.volume");
    if (!complement.value) errors[`${index}_value`] = t("expense.form.error.value");
  });

  return errors;
};

const buildGeneralValueError = (form: FormState, t: TranslateFn): string | undefined => {
  if (!form.value) return t("expense.form.error.value");
  if (parseCurrencyBRL(form.value) < 0.01) return t("expense.form.error.valuePositive");
  return undefined;
};

const buildEntityErrorMessages = (t: TranslateFn) => ({
  branchRequired: t("expense.form.error.branch"),
  branchInactive: t("expense.form.error.branchInactive"),
  segmentInactive: t("expense.form.error.segmentInactive"),
  supplierInactive: t("expense.form.error.supplierInactive"),
  categoryRequired: t("expense.form.error.category"),
  categoryInactive: t("expense.form.error.categoryInactive"),
  subcategoryInactive: t("expense.form.error.subcategoryInactive"),
});

const buildTollErrors = (form: FormState, showInputsTags: boolean, t: TranslateFn): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!form.date) errors.date = t("expense.form.error.date");
  else if (!moment(form.date, "YYYY-MM-DD", true).isValid()) errors.date = t("expense.form.error.dateInvalid");

  if (!form.startTime) errors.startTime = t("expense.form.error.startTime");

  if (!form.tollLocation) errors.tollLocation = t("expense.form.error.tollLocation");

  if (!form.concessionaire) errors.concessionaire = t("expense.form.error.concessionaire");

  if (showInputsTags) {
    if (!form.tagType) errors.tagType = t("expense.form.error.tagType");

    if (!form.tagNumber) errors.tagNumber = t("expense.form.error.tagNumber");
  }

  return errors;
};

interface IExpenseFormProps {
  branchs: InactiveSelectOption[];
  segments: InactiveSelectOption[];
  suppliers: InactiveSelectOption[];
  drivers: TripDriverOption[] & SelectOption[];
  trips?: SelectOption[];
  tripsData?: RioTrip[];
  onTripFiltersChange?: (filters: RioTripsFilters) => void;
  onTripsLoadMore?: () => void;
  hasMoreTrips?: boolean;
  isLoadingMoreTrips?: boolean;
  categories: InactiveSelectOption[];
  vehicles: SelectOption[];
  subCategories: InactiveSelectOption[];
  fileName?: string;
  openFormModal: (toggle: boolean) => void;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  onCreateNewOption: (name: NameInputChange) => void;
  onSave: (form: FormState) => void;
  isEdit?: boolean;
  onViewRecurrence?: () => void;
  handleSubmitNew?: (form: FormState) => void;
  handleFile?: (file: File) => void;
  handleClearFile: () => void;
  changeFileName: (fileName: string) => void;
  onInactiveSelect?: (item: InactiveSelectOption, fieldName: string) => void;
  fileNameOdometer?: string;
  changeFileNameOdometer: (fileName: string) => void;
  handleFileOdometer?: (file: File) => void;
  handleClearFileOdometer: () => void;
  isLoadingSubmit: boolean;
  paymentMethods?: (InactiveSelectOption & { name?: string })[];
  transactionId?: string;
  onDownloadFile?: (transactionId: string) => void;
  hasNewAttachment?: boolean;
  onDownloadOdometerFile?: (transactionId: string) => void;
  hasNewOdometerAttachment?: boolean;
}

const ExpenseForm = ({
  branchs,
  segments,
  suppliers,
  drivers,
  trips = [],
  tripsData = [],
  onTripFiltersChange,
  onTripsLoadMore,
  hasMoreTrips = false,
  isLoadingMoreTrips = false,
  categories,
  vehicles,
  subCategories,
  openFormModal,
  form,
  setForm,
  onCreateNewOption,
  onSave,
  isEdit,
  onViewRecurrence,
  handleSubmitNew,
  handleFile,
  handleClearFile,
  fileName,
  changeFileName,
  changeFileNameOdometer,
  fileNameOdometer,
  onInactiveSelect,
  handleFileOdometer,
  handleClearFileOdometer,
  isLoadingSubmit,
  paymentMethods = [],
  transactionId,
  onDownloadFile,
  hasNewAttachment,
  onDownloadOdometerFile,
  hasNewOdometerAttachment,
}: IExpenseFormProps) => {
  const t = useT();
  const { locale } = useIntl();
  const momentLocale = resolveMomentLocale(locale);
  moment.locale(momentLocale);

  const newOption = {
    id: "other",
    label: t("common.action.createNew"),
  };
  const DRIVER_LIMIT_TOOLTIP = t("expense.form.driver.tooltip");

  const COMPLEMENT_TYPES: SelectOption[] = [
    { id: "arla", label: t("expense.form.complementType.arla") },
    { id: "aditivo", label: t("expense.form.complementType.aditivo") },
    { id: "oleo", label: t("expense.form.complementType.oleo") },
  ];

  const STATUS_OPTIONS: SelectOption[] = [
    { id: "previsto", label: t("expense.status.previsto") },
    { id: "realizado", label: t("expense.status.realizado") },
    { id: "vencido", label: t("expense.status.vencido") },
  ];

  const MAINTENANCE_TYPE_OPTIONS: SelectOption[] = MAINTENANCE_TYPES.map((type) => ({
    id: type,
    label: t(`maintenance.type.${type}`),
  }));

  const REPEAT_EVERY_OPTIONS = buildRepeatEveryOptions(t, "expense");

  const WEEK_DAY_OPTIONS = buildWeekDayOptions(t, "expense");

  const getDriverLabel = (index: number) => {
    if (index === 0) return t("expense.form.driver.first", { max: MAX_DRIVERS });
    if (index === 1) return t("expense.form.driver.second", { max: MAX_DRIVERS });
    return t("expense.form.driver.last", { max: MAX_DRIVERS });
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputOdometerRef = useRef<HTMLInputElement | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [complementErrors, setComplementErrors] = useState<Record<string, string>>({});
  const [errorFile, setErrorFile] = useState("");
  const [errorFileOdometer, setErrorFileOdometer] = useState("");
  const [showComplementForm, setShowComplementForm] = useState(false);
  const [showInputsTags, setShowInputsTags] = useState(!!form.tagType);

  const { userCoords } = useContext(CoreContext);

  const [stopLocationSuggestions, setStopLocationSuggestions] = useState<AutoSuggestSuggestion[]>([]);
  const [isLoadingStopLocation, setIsLoadingStopLocation] = useState(false);
  const stopLocationDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopLocationRequestIdRef = useRef(0);

  const [tollLocationSuggestions, setTollLocationSuggestions] = useState<AutoSuggestSuggestion[]>([]);
  const [isLoadingTollLocation, setIsLoadingTollLocation] = useState(false);
  const tollLocationDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tollLocationRequestIdRef = useRef(0);

  useEffect(() => {
    return () => {
      if (stopLocationDebounceRef.current) clearTimeout(stopLocationDebounceRef.current);
      if (tollLocationDebounceRef.current) clearTimeout(tollLocationDebounceRef.current);
    };
  }, []);

  const normalizedCategoryName = useCallback(
    (categoryId?: string) => {
      const categoria = categories.find((c) => c.id === categoryId);
      return normalizeText(categoria?.rawName ?? (categoria?.label as string));
    },
    [categories]
  );

  const isStartDateCategory = useCallback(
    (categoryId?: string) => {
      const categoryName = normalizedCategoryName(categoryId);
      return categoryName.includes("pedagio") || categoryName.includes("abastecimento");
    },
    [normalizedCategoryName]
  );

  const selectedCategoryLabelNormalized = normalizedCategoryName(form.category);
  const isTollCategorySelected = selectedCategoryLabelNormalized.includes("pedagio");
  const isFuelCategorySelected = selectedCategoryLabelNormalized.includes("abastecimento");
  const paymentMethodOptions = paymentMethods.filter(
    (option) =>
      String(option.id) === String(form.paymentMethod ?? "") ||
      (!option.inactive && (!form.category || isTollCategorySelected || !isVehicleTagPaymentMethod(option)))
  );
  const isMaintenanceCategorySelected = isMaintenanceCategoryName(selectedCategoryLabelNormalized);
  const firstRowClassName = isMaintenanceCategorySelected ? EQUAL_COLUMNS_ROW_CLASS : "display-flex gap-15";
  const firstRowColumnClassName = isMaintenanceCategorySelected ? EQUAL_COLUMN_CLASS : "flex-1-1";
  const isVehicleRequired = isTollCategorySelected || isFuelCategorySelected;
  const isManualSource = isManualLikeSource(form.source);
  const isStatusOnlyEdit = !!isEdit && isMarketplaceTransaction(form.source);
  const isRecurrenceBlockedCategory = isTollCategorySelected || isFuelCategorySelected;
  const showsDueDateField = !isRecurrenceBlockedCategory;
  const showsRecurrenceBlock = isManualSource && !isRecurrenceBlockedCategory;
  const isRecurrenceActive = showsRecurrenceBlock && !!form.isRecurring;
  const showsViewRecurrenceButton = isEdit && !isRecurrenceBlockedCategory && !!form.isRecurring;
  const startReferenceField = isFuelCategorySelected ? "startDate" : "date";
  const startReferenceDate = form[startReferenceField];
  const statusDateField = isRecurrenceBlockedCategory ? startReferenceField : "dueDate";

  const overdueDueDateError =
    showsDueDateField && hasInvalidOverdueDueDate(form.status, form.dueDate)
      ? t("expense.form.error.overdueDueDate", { date: moment().format("DD/MM/YYYY") })
      : undefined;

  useEffect(() => {
    if (!isRecurrenceBlockedCategory) return;

    setForm((prev) => (prev.isRecurring ? { ...prev, isRecurring: false } : prev));
  }, [isRecurrenceBlockedCategory, setForm]);

  useEffect(() => {
    if (isEdit) return;

    setForm((prev) => (isStartDateCategory(prev.category) ? prev : withStatusFromDate(prev, form.dueDate)));
  }, [isEdit, form.dueDate, isStartDateCategory, setForm]);

  useEffect(() => {
    if (isEdit || isRecurrenceBlockedCategory || !form.category) return;

    setForm((prev) => withStatusFromDate(prev, prev.dueDate));
  }, [isEdit, isRecurrenceBlockedCategory, form.category, setForm]);

  useEffect(() => {
    if (isEdit || !isRecurrenceBlockedCategory || form.isStatusManual) return;

    setForm((prev) => withStatusFromDate(prev, startReferenceDate));
  }, [isEdit, isRecurrenceBlockedCategory, form.isStatusManual, startReferenceDate, setForm]);

  useEffect(() => {
    if (form.repeatEvery !== "ano") return;
    setForm((prev) => {
      const anchor = prev.dueDate;
      const start = prev.recurrenceStart
        ? (toYearAnchoredDate(prev.recurrenceStart, anchor) ?? prev.recurrenceStart)
        : prev.recurrenceStart;
      const end = prev.recurrenceEnd
        ? (toYearAnchoredDate(prev.recurrenceEnd, anchor) ?? prev.recurrenceEnd)
        : prev.recurrenceEnd;
      if (start === prev.recurrenceStart && end === prev.recurrenceEnd) return prev;
      return { ...prev, recurrenceStart: start, recurrenceEnd: end };
    });
  }, [form.repeatEvery, form.dueDate, setForm]);

  useEffect(() => {
    if (isEdit || isFuelCategorySelected || isTollCategorySelected) return;

    setForm((prev) => (prev.date ? prev : { ...prev, date: moment().format("YYYY-MM-DD") }));
  }, [isEdit, isFuelCategorySelected, isTollCategorySelected, form.date, setForm]);

  const tripSelection = resolveTripSelection(
    tripsData?.find((trip) => trip.id === form.trip),
    drivers ?? [],
    vehicles ?? []
  );

  const isDriverLockedByTrip = !!tripSelection?.driverMatched;
  const isVehicleLockedByTrip = !!tripSelection?.vehicleMatched;

  const [tripSearch, setTripSearch] = useState("");
  const tripSearchAplicado = useDebouncedValue(tripSearch, TRIP_SEARCH_DELAY_MS);

  const rioDriverIdsSelecionados = useMemo(
    () => resolveRioDriverIds(form.driver, drivers ?? []),
    [form.driver, drivers]
  );

  const tripFilters = useMemo(
    () =>
      buildTripFilters({
        search: tripSearchAplicado,
        vehicleId: form.vehicle,
        driverIds: rioDriverIdsSelecionados,
        date: form.dueDate,
      }),
    [tripSearchAplicado, form.vehicle, rioDriverIdsSelecionados, form.dueDate]
  );

  useTripFiltersRequest(
    {
      search: tripSearchAplicado,
      vehicleId: form.vehicle,
      driverIds: rioDriverIdsSelecionados,
      date: form.dueDate,
    },
    onTripFiltersChange
  );

  useEffect(() => {
    if (!form.trip) return;

    const viagemSelecionada = tripsData?.find((trip) => trip.id === form.trip);
    if (!viagemSelecionada) return;
    if (tripMatchesFilters(viagemSelecionada, tripFilters)) return;

    setForm((prev) => ({ ...prev, trip: "" }));
  }, [form.trip, tripsData, tripFilters, setForm]);

  useEffect(() => {
    if (isEdit) return;
    if (!isTollCategorySelected) return;
    if (!isTruckerPayTagSupplier(suppliers, form.supplier)) return;
    setForm((prev) => ({ ...prev, supplier: "" }));
  }, [isEdit, isTollCategorySelected, form.supplier, suppliers, setForm]);

  useEffect(() => {
    setShowComplementForm(!!isEdit && (form.complements?.length ?? 0) > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, transactionId]);

  const formatDistance = (meters?: number): string => {
    if (meters === undefined || !Number.isFinite(meters)) return "";
    if (meters < 1000) return `${Math.round(meters)} m`;
    const km = meters / 1000;
    const rounded = km >= 100 ? Math.round(km) : Math.round(km * 10) / 10;
    return `${rounded.toString().replace(".", ",")} km`;
  };

  const toSuggestionItems = (items: AddressSuggestion[]): AutoSuggestSuggestion[] =>
    items
      .filter((item) => !!item.label)
      .map((item) => ({
        label: item.label,
        address: item.address ?? "",
        id: item.id ?? "",
        distance: formatDistance(item.distanceMeters),
        lat: item.lat?.toString() ?? "",
        lon: item.lon?.toString() ?? "",
      }));

  const fetchStopLocationSuggestions = (value: string) => {
    const trimmed = value.trim();

    if (stopLocationDebounceRef.current) clearTimeout(stopLocationDebounceRef.current);

    if (trimmed.length < 3) {
      setStopLocationSuggestions([]);
      setIsLoadingStopLocation(false);
      return;
    }

    setIsLoadingStopLocation(true);
    const requestId = ++stopLocationRequestIdRef.current;

    stopLocationDebounceRef.current = setTimeout(async () => {
      try {
        const results = await getAddressRouting(trimmed, userCoords);
        if (requestId !== stopLocationRequestIdRef.current) return;
        setStopLocationSuggestions(toSuggestionItems(results));
      } catch {
        if (requestId !== stopLocationRequestIdRef.current) return;
        setStopLocationSuggestions([]);
      } finally {
        if (requestId === stopLocationRequestIdRef.current) {
          setIsLoadingStopLocation(false);
        }
      }
    }, 300);
  };

  const handleStopLocationFetch = ({ value }: { value: string }) => {
    setForm((prev) => ({ ...prev, stopLocation: value }));
    fetchStopLocationSuggestions(value);
  };

  const renderStopLocationSuggestion = (suggestion: AutoSuggestSuggestion) => (
    <div className="display-flex align-items-center gap-10 width-100pct">
      <span className="rioglyph rioglyph-poi text-size-18 text-color-primary" aria-hidden="true" />
      <div className="display-flex flex-column flex-1-1 min-width-0">
        <span className="text-size-14 ellipsis-1">{suggestion.label}</span>
        {suggestion.address ? (
          <span className="text-size-12 text-color-gray ellipsis-1">{suggestion.address}</span>
        ) : null}
      </div>
      {suggestion.distance ? (
        <span className="text-size-12 text-color-gray text-right white-space-nowrap margin-left-10">
          {suggestion.distance}
        </span>
      ) : null}
    </div>
  );

  const fetchTollLocationSuggestions = (value: string) => {
    const trimmed = value.trim();

    if (tollLocationDebounceRef.current) clearTimeout(tollLocationDebounceRef.current);

    if (trimmed.length < 3) {
      setTollLocationSuggestions([]);
      setIsLoadingTollLocation(false);
      return;
    }

    setIsLoadingTollLocation(true);
    const requestId = ++tollLocationRequestIdRef.current;

    tollLocationDebounceRef.current = setTimeout(async () => {
      try {
        const results = await getAddressRouting(trimmed, userCoords);
        if (requestId !== tollLocationRequestIdRef.current) return;
        setTollLocationSuggestions(toSuggestionItems(results));
      } catch {
        if (requestId !== tollLocationRequestIdRef.current) return;
        setTollLocationSuggestions([]);
      } finally {
        if (requestId === tollLocationRequestIdRef.current) {
          setIsLoadingTollLocation(false);
        }
      }
    }, 300);
  };

  const handleTollLocationFetch = ({ value }: { value: string }) => {
    setForm((prev) => ({ ...prev, tollLocation: value }));
    fetchTollLocationSuggestions(value);
  };

  const { driversQuantity, setDriversQuantity, inputDriverChange, inputClearDriver } = useDriverSlots(
    form?.driver,
    setForm,
    MAX_DRIVERS
  );

  const handleTripChange = (value?: string) => {
    const selection = resolveTripSelection(
      tripsData?.find((trip) => trip.id === value),
      drivers ?? [],
      vehicles ?? []
    );

    if (selection?.driverMatched) setDriversQuantity(1);

    setForm((prev) => ({
      ...prev,
      trip: value ?? "",
      ...(selection?.driverMatched ? { driver: [selection.driverId] } : {}),
      ...(selection?.vehicleMatched ? { vehicle: selection.vehicleId } : {}),
    }));
  };

  useEffect(() => {
    clearFileInputValue(fileInputRef);
  }, [fileName]);

  useEffect(() => {
    clearFileInputValue(fileInputOdometerRef);
  }, [fileInputOdometerRef]);

  const handleButtonClick = (TYPE: "DEFAULT" | "ODOMETER") => {
    if (TYPE === "ODOMETER") {
      fileInputOdometerRef.current?.click();
      return;
    }

    fileInputRef.current?.click();
  };

  const attachmentMessages = {
    tooLarge: t("expense.form.error.fileSizeTooLarge"),
    invalidType: t("expense.form.error.fileFormatInvalid"),
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    processAttachmentFileChange(event, attachmentMessages, {
      setError: setErrorFile,
      changeFileName,
      onFile: handleFile,
    });
  };

  const handleFileOdometerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    processAttachmentFileChange(event, attachmentMessages, {
      setError: setErrorFileOdometer,
      changeFileName: changeFileNameOdometer,
      onFile: handleFileOdometer,
    });
  };

  const inputChange = (name: NameInputChange | keyof FormState, value?: string) => {
    const limpaDataDoPedagio = name === "category" && normalizedCategoryName(value).includes("pedagio");
    const limpaStatus = !isEdit && (name === "category" || name === statusDateField);
    const limpaTipoDeManutencao = name === "category" && !isMaintenanceCategoryName(normalizedCategoryName(value));
    const semTipoDeManutencao = limpaTipoDeManutencao ? { maintenanceType: "" } : {};
    const limpaTagDoVeiculo = name === "category" && !!value && !normalizedCategoryName(value).includes("pedagio");
    const semTagDoVeiculo = (paymentMethod?: string) =>
      limpaTagDoVeiculo &&
      paymentMethods.some(
        (option) => String(option.id) === String(paymentMethod ?? "") && isVehicleTagPaymentMethod(option)
      )
        ? { paymentMethod: "" }
        : {};

    if (value) {
      const nextValue = name === "value" ? formatCurrency(value, false) : value;
      setForm((prev) => {
        const atualizado = applyFuelPriceLink({ ...prev, [name]: nextValue }, name, nextValue);
        return {
          ...atualizado,
          ...(limpaDataDoPedagio ? { date: "" } : {}),
          ...semTipoDeManutencao,
          ...semTagDoVeiculo(prev.paymentMethod),
        };
      });
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
        ...(limpaStatus ? { status: "", isStatusManual: false } : {}),
        ...semTipoDeManutencao,
        ...semTagDoVeiculo(prev.paymentMethod),
      }));
    }

    setErrors((prev) => ({
      ...prev,
      [name]: "",
      ...semTipoDeManutencao,
    }));
  };

  const handleStatusChange = (name: NameInputChange, value?: string) => {
    inputChange(name, value);
    setForm((prev) => ({ ...prev, isStatusManual: !!value }));
  };

  const handleSubmit = () => {
    const newErrors = validate();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(form);
  };

  const handleSubmitAndCreateNew = () => {
    const newErrors = validate();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    handleSubmitNew?.(form);
  };

  const validate = () => {
    if (isStatusOnlyEdit) return overdueDueDateError ? { dueDate: overdueDueDateError } : {};

    const newErrors: Record<string, string> = {};

    const selectedCategory = categories.find((c) => c.id === form.category);
    const categoryName = normalizeText(selectedCategory?.rawName ?? (selectedCategory?.label as string));
    const isFuelCategory = categoryName.includes("abastecimento");
    const isTollCategory = categoryName.includes("pedagio");

    if (isMaintenanceCategoryName(categoryName) && !form.maintenanceType) {
      newErrors.maintenanceType = t("expense.form.error.maintenanceType");
    }

    if (isFuelCategory) {
      const selectedSubcategory = subCategories.find((s) => s.id === form.subcategory);
      const subcategoryLabel = normalizeText(selectedSubcategory?.rawName ?? (selectedSubcategory?.label as string));

      Object.assign(newErrors, buildFuelErrors(form, subcategoryLabel === "hibrido", t));
      setComplementErrors(buildComplementErrors(form.complements, t));
    } else {
      const valueError = buildGeneralValueError(form, t);
      if (valueError) newErrors.value = valueError;
    }

    if (!isFuelCategory && !isTollCategory && !form.dueDate) {
      newErrors.dueDate = t("expense.form.error.dueDate");
    }

    Object.assign(
      newErrors,
      buildEntityErrors(form, { branchs, segments, suppliers, categories, subCategories }, buildEntityErrorMessages(t))
    );

    if (!form.paymentMethod) newErrors.paymentMethod = t("expense.form.error.paymentMethod");

    if (isRecurrenceActive) {
      const periodErrorKey = getRecurrencePeriodErrorKey(form.recurrenceStart || form.dueDate, form.recurrenceEnd);
      if (periodErrorKey) newErrors.recurrenceEnd = t(periodErrorKey);
    }

    if (overdueDueDateError) newErrors.dueDate = overdueDueDateError;

    if ((isFuelCategory || isTollCategory) && !form.vehicle) {
      newErrors.vehicle = t("expense.form.error.vehicle");
    }

    if (isTollCategory) {
      Object.assign(newErrors, buildTollErrors(form, showInputsTags, t));
    }

    return newErrors;
  };

  const fileClear = (TYPE: "DEFAULT" | "ODOMETER") => {
    if (TYPE === "ODOMETER") {
      changeFileNameOdometer("");
      handleClearFileOdometer();
      return;
    }

    changeFileName("");
    handleClearFile();
  };

  const renderAttachmentChip = (index: number, onRemove: () => void, canDownload: boolean, onDownload: () => void) => (
    <span
      className="btn btn-muted btn-sm rounded-small align-items-center gap-10"
      style={{
        display: "inline-flex",
        maxWidth: "100%",
        width: "fit-content",
        boxSizing: "border-box",
        marginTop: 10,
      }}
    >
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {t("common.label.attachmentIndex", { n: index })}
      </span>
      {canDownload && (
        <button
          type="button"
          tabIndex={0}
          title={t("expense.form.action.downloadReceipt")}
          className="rioglyph rioglyph-download cursor-pointer text-color-primary"
          style={ATTACHMENT_ICON_STYLE}
          onClick={onDownload}
          onKeyDown={(evento) => {
            if (evento.key === "Enter" || evento.key === " ") {
              evento.preventDefault();
              onDownload();
            }
          }}
        />
      )}
      {!isStatusOnlyEdit && (
        <button
          type="button"
          tabIndex={0}
          title={t("expense.form.action.deleteReceipt")}
          className="rioglyph rioglyph-remove-sign cursor-pointer text-color-danger"
          style={ATTACHMENT_ICON_STYLE}
          onClick={onRemove}
          onKeyDown={(evento) => {
            if (evento.key === "Enter" || evento.key === " ") {
              evento.preventDefault();
              onRemove();
            }
          }}
        />
      )}
    </span>
  );

  type LightKeys = "990" | "938" | "991";

  const light: Record<LightKeys, string> = {
    "990": "(Com operador)",
    "938": "(Tag, Sem Parar, etc...)",
    "991": "(Free-flow)",
  };
  const complementList = form.complements ?? [];

  const clearComplementError = (errorKey: string) => {
    setComplementErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[errorKey];
      return newErrors;
    });
  };

  const applyComplementChange = (index: number, updated: Complement) => {
    const newComplements = [...complementList];
    newComplements[index] = updated;
    setForm((prev) => ({ ...prev, complements: newComplements }));
  };

  const changeComplementType = (index: number, typeId: string) => {
    applyComplementChange(index, { ...complementList[index], type: typeId });
    clearComplementError(`${index}_type`);
  };

  const changeComplementVolume = (index: number, volume: string) => {
    const updated = { ...complementList[index], volume };

    if (updated.value) {
      updated.unitPrice = calculateFuelUnitPrice(updated.value, volume);
    }

    applyComplementChange(index, updated);
    clearComplementError(`${index}_volume`);
  };

  const changeComplementValue = (index: number, rawValue: string) => {
    const formattedValue = formatCurrency(rawValue, false);
    const updated = { ...complementList[index], value: formattedValue };

    if (updated.volume) {
      updated.unitPrice = calculateFuelUnitPrice(formattedValue, updated.volume);
    }

    applyComplementChange(index, updated);
    clearComplementError(`${index}_value`);
  };

  const removeComplement = (index: number) => {
    const newComplements = form.complements?.filter((_, i) => i !== index);
    setForm((prev) => ({ ...prev, complements: newComplements }));
    return newComplements;
  };

  const removeComplementAndCloseForm = (index: number) => {
    const newComplements = removeComplement(index);

    if (!newComplements || newComplements.length === 0) {
      setShowComplementForm(false);
    }
  };

  const addComplement = () => {
    const newComplement: Complement = {
      id: criarComplementoId(),
      type: "",
      volume: "",
      value: "",
      unitPrice: "",
    };
    setForm((prev) => ({
      ...prev,
      complements: [...(prev.complements || []), newComplement],
    }));
  };

  const complementLabels = {
    type: t("expense.form.label.complementType"),
    volume: t("expense.form.label.volume"),
    unitPrice: t("expense.form.label.complementUnitPrice"),
    selectPlaceholder: t("common.placeholder.select"),
  };

  const newSubCategories = subCategories.map((res) => {
    const suffix = light[res.id as keyof typeof light];
    if (!suffix || typeof res.label !== "string") return { ...res };
    return { ...res, label: res.label + suffix };
  });

  const subcategoryField = (
    <InputSelect
      disabled={!form.category || (!!isEdit && !!subCategories.find((s) => s.id === form.subcategory)?.inactive)}
      inputChange={inputChange}
      label={t("expense.form.label.subcategory")}
      name="subcategory"
      newOption={newOption}
      onCreateNewOption={onCreateNewOption}
      options={newSubCategories}
      value={form.subcategory}
      errorMessage={errors.subcategory}
      onInactiveSelect={(item) => onInactiveSelect?.(item, "subcategory")}
      inactiveEntityLabel="subcategoria"
      useClear
    />
  );

  return (
    <>
      <div className="padding-x-20">
        <form>
          <fieldset>
            <div className="">
              {showsViewRecurrenceButton && (
                <div className="margin-bottom-20">
                  <button
                    type="button"
                    className="btn btn-default text-color-darker border-color-light hover-bg-lighter hover-text-color-darker hover-border-color-light"
                    onClick={onViewRecurrence}
                  >
                    <span className="rioglyph rioglyph-calendar" aria-hidden="true" />
                    {t("expense.form.viewRecurrence")}
                  </button>
                </div>
              )}

              <div className={firstRowClassName}>
                <div className={firstRowColumnClassName}>
                  <InputSelect
                    disabled={
                      isStatusOnlyEdit || (!!isEdit && !!categories.find((c) => c.id === form.category)?.inactive)
                    }
                    inputChange={inputChange}
                    label={t("expense.form.label.category")}
                    name="category"
                    newOption={newOption}
                    onCreateNewOption={onCreateNewOption}
                    options={categories}
                    value={form.category}
                    errorMessage={errors.category}
                    onInactiveSelect={(item) => onInactiveSelect?.(item, "category")}
                    inactiveEntityLabel="categoria"
                    useClear
                  />
                </div>
                <div className={firstRowColumnClassName}>
                  <InputSelect
                    inputChange={handleStatusChange}
                    label={t("expense.form.label.status")}
                    name="status"
                    onCreateNewOption={() => {}}
                    options={STATUS_OPTIONS}
                    value={form.status ?? ""}
                    useClear
                  />
                </div>
              </div>
            </div>
          </fieldset>

          <fieldset disabled={isStatusOnlyEdit}>
            <div className="">
              {isMaintenanceCategorySelected ? (
                <div className={EQUAL_COLUMNS_ROW_CLASS}>
                  <div className={EQUAL_COLUMN_CLASS}>{subcategoryField}</div>
                  <div className={EQUAL_COLUMN_CLASS}>
                    <InputSelect
                      disabled={isStatusOnlyEdit}
                      inputChange={inputChange}
                      label={t("expense.form.label.maintenanceType")}
                      name="maintenanceType"
                      onCreateNewOption={() => {}}
                      options={MAINTENANCE_TYPE_OPTIONS}
                      value={form.maintenanceType ?? ""}
                      errorMessage={errors.maintenanceType}
                      useClear
                    />
                  </div>
                </div>
              ) : (
                subcategoryField
              )}

              <hr className="margin-y-20" />

              {showsDueDateField && (
                <div className="display-flex gap-15">
                  <DueDateField
                    className="flex-1-1 form-group"
                    label={t("expense.form.label.dueDate")}
                    placeholder={t("common.placeholder.selectDate")}
                    momentLocale={momentLocale}
                    value={form.dueDate}
                    errorMessage={errors.dueDate ?? overdueDueDateError}
                    onChange={(value) => inputChange("dueDate", value)}
                  />
                  {showsRecurrenceBlock && (
                    <div className="form-group display-flex flex-column">
                      <span aria-hidden="true" className="display-block text-size-12 margin-bottom-5">
                        &nbsp;
                      </span>
                      <div className="display-flex flex-row flex-1-1 align-items-center gap-5">
                        <label className="margin-0 text-size-12 text-color-gray white-space-nowrap">
                          {t("expense.form.label.recurring")}
                        </label>
                        <Switch
                          checked={!!form.isRecurring}
                          onChange={(checked) => setForm((prev) => ({ ...prev, isRecurring: checked }))}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isRecurrenceActive && (
                <RecurrenceFields
                  className="display-flex flex-wrap align-items-end gap-10"
                  labels={{
                    repeatEvery: t("expense.form.label.repeatEvery"),
                    weekDays: t("expense.form.label.weekDays"),
                    recurrenceStartYear: t("expense.form.label.recurrenceStartYear"),
                    recurrenceEndYear: t("expense.form.label.recurrenceEndYear"),
                    recurrenceStart: t("expense.form.label.recurrenceStart"),
                    recurrenceEnd: t("expense.form.label.recurrenceEnd"),
                    datePlaceholder: t("common.placeholder.selectDate"),
                  }}
                  repeatEveryOptions={REPEAT_EVERY_OPTIONS}
                  weekDayOptions={WEEK_DAY_OPTIONS}
                  momentLocale={momentLocale}
                  repeatEvery={form.repeatEvery}
                  weekDays={form.weekDays}
                  recurrenceStart={form.recurrenceStart}
                  recurrenceEnd={form.recurrenceEnd}
                  recurrenceEndError={errors.recurrenceEnd}
                  dueDate={form.dueDate}
                  inputChange={inputChange}
                  onWeekDaysChange={(weekDays) => setForm((prev) => ({ ...prev, weekDays }))}
                />
              )}

              {isTollCategorySelected && (
                <div className="display-flex gap-15">
                  <div className={`flex-1-1 form-group ${errorClass(errors.date)}`}>
                    <label htmlFor="inputDate">{t("expense.form.label.startDate")}</label>
                    <DatePicker
                      id="inputDate"
                      dateFormat="DD/MM/YYYY"
                      locale={momentLocale}
                      onChange={(val) => {
                        const formatted = typeof val === "string" ? val : val.format("YYYY-MM-DD");
                        inputChange("date", formatted);
                      }}
                      value={form.date ? moment(form.date).format("DD/MM/YYYY") : ""}
                      className="margin-0"
                      timeFormat={false}
                      inputProps={{ placeholder: t("common.placeholder.selectDate") }}
                    />
                    {renderErrorIcon(errors.date)}
                    <span className="help-block">
                      <span>{errors.date}</span>
                    </span>
                  </div>
                  <div className={`flex-1-1 form-group ${errorClass(errors.startTime)}`}>
                    <label htmlFor="startTime">{t("expense.form.label.startTime")}</label>
                    <TimePicker
                      onChange={(val) => inputChange("startTime", val)}
                      value={form.startTime || ""}
                      className="margin-0"
                    />
                    {renderErrorIcon(errors.startTime)}
                    <span className="help-block">
                      <span>{errors.startTime}</span>
                    </span>
                  </div>
                </div>
              )}

              {(() => {
                const selectedCategory = categories.find((c) => c.id === form.category);
                const isToll = normalizeText(selectedCategory?.rawName ?? (selectedCategory?.label as string)).includes(
                  "pedagio"
                );

                if (isToll) {
                  return (
                    <div className="border border-left-none border-right-none padding-y-15 margin-bottom-15">
                      <div className={`form-group ${errorClass(errors.tollLocation)}`}>
                        <label htmlFor="tollLocation">{t("expense.form.label.tollLocation")}</label>
                        <AutoSuggest
                          id="tollLocation"
                          inputProps={{
                            id: "tollLocation",
                            placeholder: t("common.placeholder.typeHere"),
                            value: form.tollLocation || "",
                            hasError: !!errors.tollLocation,
                            disabled: isEdit,
                          }}
                          suggestions={tollLocationSuggestions}
                          onSuggestionsFetchRequested={handleTollLocationFetch}
                          onSuggestionSelected={(_event, { suggestion }) => {
                            const lat = suggestion.lat as string;
                            const lon = suggestion.lon as string;
                            const coordinates = lat && lon ? `${lat}, ${lon}` : "";
                            const label = (suggestion.label as string) || "";
                            const plazaFromLabel = label.split(",")[0].trim();
                            setForm((prev) => ({
                              ...prev,
                              tollLocation: label,
                              tollCoordinates: coordinates,
                              tollLatitude: lat || "",
                              tollLongitude: lon || "",
                              plaza: plazaFromLabel,
                            }));
                            setTollLocationSuggestions([]);
                          }}
                          renderSuggestion={renderStopLocationSuggestion}
                          isLoading={isLoadingTollLocation}
                          noItemMessage={t("common.placeholder.noResults")}
                          closeOnSelect
                        />
                        {renderErrorIcon(errors.tollLocation)}
                        <span className="help-block">
                          <span>{errors.tollLocation}</span>
                        </span>
                      </div>

                      <div className={`form-group ${errorClass(errors.concessionaire)}`}>
                        <label htmlFor="stopLocation">{t("expense.form.label.concessionaire")}</label>
                        <input
                          id="concessionaire"
                          type="text"
                          className="form-control"
                          disabled={isEdit}
                          value={form.concessionaire || ""}
                          onChange={(e) => inputChange("concessionaire", e.target.value)}
                          placeholder={t("common.placeholder.typeHere")}
                        />
                        {renderErrorIcon(errors.concessionaire)}
                        <span className="help-block">
                          <span>{errors.concessionaire}</span>
                        </span>
                      </div>

                      <div className="form-group">
                        <label htmlFor="plaza">{t("expense.form.label.plaza")}</label>
                        <input
                          id="plaza"
                          type="text"
                          className="form-control"
                          disabled={!!isEdit && !!form.tollIsIntegration}
                          value={form.plaza || ""}
                          onChange={(e) => inputChange("plaza", e.target.value)}
                          placeholder={t("common.placeholder.typeHere")}
                        />
                      </div>

                      {(() => {
                        const isTruckerPayTag = isTruckerPayTagSupplier(suppliers, form.supplier);
                        if (isTruckerPayTag && !isEdit) return null;
                        return (
                          <div className="form-group">
                            <label htmlFor="axesCategory">{t("expense.form.label.axesCategory")}</label>
                            <Select
                              id="axesCategory"
                              options={AXES_CATEGORY_OPTIONS}
                              value={form.axesCategory ? [form.axesCategory] : []}
                              onChange={(val) => inputChange("axesCategory", val?.id ?? "")}
                              placeholder={t("common.placeholder.select")}
                              disabled={isTruckerPayTag}
                              useClear={!isTruckerPayTag}
                            />
                          </div>
                        );
                      })()}

                      <div className="border border-top-only padding-top-15">
                        {showInputsTags ? (
                          <>
                            <div className="margin-bottom-10">
                              <span className="text-size-16 text-color-primary">
                                {t("expense.form.label.additionalTag")}
                              </span>
                            </div>

                            <div className={`form-group ${errorClass(errors.tagType)}`}>
                              <label htmlFor="stopLocation">{t("expense.form.label.tagType")}</label>
                              <input
                                id="tagType"
                                type="text"
                                className="form-control"
                                value={form.tagType || ""}
                                disabled={isEdit}
                                onChange={(e) => inputChange("tagType", e.target.value)}
                                placeholder={t("common.placeholder.typeHere")}
                              />
                              {renderErrorIcon(errors.tagType)}
                              <span className="help-block">
                                <span>{errors.tagType}</span>
                              </span>
                            </div>

                            <div className={`form-group ${errorClass(errors.tagNumber)}`}>
                              <label htmlFor="stopLocation">{t("expense.form.label.tagNumber")}</label>
                              <input
                                id="tagNumber"
                                type="text"
                                className="form-control"
                                disabled={isEdit}
                                value={form.tagNumber || ""}
                                onChange={(e) => inputChange("tagNumber", e.target.value)}
                                placeholder={t("common.placeholder.typeHere")}
                              />
                              {renderErrorIcon(errors.tagNumber)}
                              <span className="help-block">
                                <span>{errors.tagNumber}</span>
                              </span>
                            </div>

                            <Button
                              className="border-none btn btn-default text-color-danger"
                              iconName="rioglyph rioglyph-trash"
                              bsStyle={Button.MUTED}
                              disabled={isEdit}
                              onClick={() => {
                                setShowInputsTags(false);
                                inputChange("tagType", "");
                                inputChange("tagNumber", "");
                              }}
                            >
                              {t("expense.form.action.removeComplement")}
                            </Button>
                          </>
                        ) : (
                          <Button onClick={() => setShowInputsTags(true)}>{t("expense.form.action.addTag")}</Button>
                        )}
                      </div>
                    </div>
                  );
                }

                return null;
              })()}

              {(() => {
                const selectedCategory = categories.find((c) => c.id === form.category);
                const isFuelCategory = normalizeText(
                  selectedCategory?.rawName ?? (selectedCategory?.label as string)
                ).includes("abastecimento");
                const selectedSubcategory = subCategories.find((s) => s.id === form.subcategory);
                const subcategoryLabel = normalizeText(
                  selectedSubcategory?.rawName ?? (selectedSubcategory?.label as string)
                );
                const isHybridSubcategory = subcategoryLabel === "hibrido";
                const fuelUnitLabel = getFuelUnitLabel(subcategoryLabel);
                const consumptionUnitLabel = getConsumptionUnitLabel(subcategoryLabel);

                if (!isFuelCategory) return null;

                return (
                  <>
                    <div className="display-flex gap-15">
                      <div className={`flex-1-1 form-group ${errorClass(errors.startDate)}`}>
                        <label htmlFor="startDate">{t("expense.form.label.startDate")}</label>
                        <DatePicker
                          id="startDate"
                          dateFormat="DD/MM/YYYY"
                          locale={momentLocale}
                          onChange={(val) => {
                            const formatted = typeof val === "string" ? val : val.format("YYYY-MM-DD");
                            inputChange("startDate", formatted);
                          }}
                          value={form.startDate ? moment(form.startDate).format("DD/MM/YYYY") : ""}
                          className="margin-0"
                          timeFormat={false}
                          inputProps={{ placeholder: t("common.placeholder.selectDate") }}
                        />
                        {renderErrorIcon(errors.startDate)}
                        <span className="help-block">
                          <span>{errors.startDate}</span>
                        </span>
                      </div>
                      <div className={`flex-1-1 form-group ${errorClass(errors.startTime)}`}>
                        <label htmlFor="startTime">{t("expense.form.label.startTime")}</label>
                        <TimePicker
                          onChange={(val) => inputChange("startTime", val)}
                          value={form.startTime || ""}
                          className="margin-0"
                        />
                        {renderErrorIcon(errors.startTime)}
                        <span className="help-block">
                          <span>{errors.startTime}</span>
                        </span>
                      </div>
                    </div>

                    <div className={`form-group ${errorClass(errors.stopLocation)}`}>
                      <label htmlFor="stopLocation">{t("expense.form.label.stopLocation")}</label>
                      <AutoSuggest
                        id="stopLocation"
                        inputProps={{
                          id: "stopLocation",
                          placeholder: t("common.placeholder.typeHere"),
                          value: form.stopLocation || "",
                          hasError: !!errors.stopLocation,
                        }}
                        suggestions={stopLocationSuggestions}
                        onSuggestionsFetchRequested={handleStopLocationFetch}
                        onSuggestionSelected={(_event, { suggestion }) => {
                          const lat = suggestion.lat as string;
                          const lon = suggestion.lon as string;
                          const coordinates = lat && lon ? `${lat}, ${lon}` : "";
                          setForm((prev) => ({
                            ...prev,
                            stopLocation: (suggestion.label as string) || "",
                            locationCoordinates: coordinates,
                          }));
                          setStopLocationSuggestions([]);
                        }}
                        renderSuggestion={renderStopLocationSuggestion}
                        isLoading={isLoadingStopLocation}
                        noItemMessage={t("common.placeholder.noResults")}
                        closeOnSelect
                      />
                      {renderErrorIcon(errors.stopLocation)}
                      <span className="help-block">
                        <span>{errors.stopLocation}</span>
                      </span>
                    </div>

                    <div className={`form-group ${errorClass(errors.odometerKm)}`}>
                      <label htmlFor="odometerKm">{t("expense.form.label.odometerKm")}</label>
                      <div className="input-group">
                        <span className="input-group-addon">Km</span>
                        <input
                          id="odometerKm"
                          type="text"
                          className="form-control"
                          value={form.odometerKm || ""}
                          onChange={(e) => inputChange("odometerKm", e.target.value)}
                          placeholder="1.500"
                        />
                      </div>
                      {renderErrorIcon(errors.odometerKm)}
                      <span className="help-block">
                        <span>{errors.odometerKm}</span>
                      </span>
                    </div>

                    {isHybridSubcategory && (
                      <>
                        <div className="margin-bottom-20">
                          <div className="text-color-primary text-size-16 text-medium margin-bottom-15">
                            {t("expense.form.label.fuelsUsed")}
                          </div>

                          <div className={`form-group ${errorClass(errors.firstFuelType)}`}>
                            <label>{t("expense.form.label.firstFuel")}</label>
                            <Select
                              options={[
                                { id: "diesel", label: t("fueling.fuel.diesel") },
                                { id: "eletrico", label: t("fueling.fuel.eletrico") },
                              ]}
                              value={form.firstFuelType ? [form.firstFuelType] : []}
                              onChange={(val) => inputChange("firstFuelType", val?.id)}
                              placeholder={t("common.placeholder.select")}
                              useClear
                            />
                            {renderErrorFeedback(errors.firstFuelType)}
                          </div>

                          <div className="display-flex gap-15">
                            <div className={`flex-1-1 form-group ${errorClass(errors.firstFuelVolume)}`}>
                              <label>{t("expense.form.label.volume")}</label>
                              <div className="input-group">
                                <span className="input-group-addon">L</span>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={form.firstFuelVolume || ""}
                                  onChange={(e) => inputChange("firstFuelVolume", e.target.value)}
                                  placeholder="240"
                                />
                              </div>
                              {renderErrorFeedback(errors.firstFuelVolume)}
                            </div>
                            <div className={`flex-1-1 form-group ${errorClass(errors.firstFuelValue)}`}>
                              <label>{t("expense.form.label.complementValue")}</label>
                              <div className="input-group">
                                <span className="input-group-addon">R$</span>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={form.firstFuelValue || ""}
                                  onChange={(e) => inputChange("firstFuelValue", e.target.value)}
                                  placeholder="1.560,00"
                                />
                              </div>
                              {renderErrorFeedback(errors.firstFuelValue)}
                            </div>
                          </div>

                          <div className="form-group">
                            <label>{t("expense.form.label.unitPrice")}</label>
                            <div className="input-group">
                              <span className="input-group-addon">R$</span>
                              <input
                                type="text"
                                className="form-control"
                                value={form.firstFuelUnitPrice || ""}
                                readOnly
                                placeholder="6,50"
                              />
                            </div>
                          </div>

                          <div className={`form-group ${errorClass(errors.secondFuelType)}`}>
                            <label>{t("expense.form.label.secondFuel")}</label>
                            <Select
                              options={[
                                { id: "diesel", label: t("fueling.fuel.diesel") },
                                { id: "eletrico", label: t("fueling.fuel.eletrico") },
                              ]}
                              value={form.secondFuelType ? [form.secondFuelType] : []}
                              onChange={(val) => inputChange("secondFuelType", val?.id)}
                              placeholder={t("common.placeholder.select")}
                              useClear
                            />
                            {renderErrorFeedback(errors.secondFuelType)}
                          </div>

                          <div className="display-flex gap-15">
                            <div className={`flex-1-1 form-group ${errorClass(errors.secondFuelVolume)}`}>
                              <label>{t("expense.form.label.volume")}</label>
                              <div className="input-group">
                                <span className="input-group-addon">kWh</span>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={form.secondFuelVolume || ""}
                                  onChange={(e) => inputChange("secondFuelVolume", e.target.value)}
                                  placeholder="2.276"
                                />
                              </div>
                              {renderErrorFeedback(errors.secondFuelVolume)}
                            </div>
                            <div className={`flex-1-1 form-group ${errorClass(errors.secondFuelValue)}`}>
                              <label>{t("expense.form.label.complementValue")}</label>
                              <div className="input-group">
                                <span className="input-group-addon">R$</span>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={form.secondFuelValue || ""}
                                  onChange={(e) => inputChange("secondFuelValue", e.target.value)}
                                  placeholder="1.560,00"
                                />
                              </div>
                              {renderErrorFeedback(errors.secondFuelValue)}
                            </div>
                          </div>

                          <div className="form-group">
                            <label>{t("expense.form.label.unitPrice")}</label>
                            <div className="input-group">
                              <span className="input-group-addon">R$</span>
                              <input
                                type="text"
                                className="form-control"
                                value={form.secondFuelUnitPrice || ""}
                                readOnly
                                placeholder="1,45"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="display-flex gap-15">
                          <div className={`flex-1-1 form-group ${errorClass(errors.totalVolume)}`}>
                            <label>{t("expense.form.label.totalVolume")}</label>
                            <div className="input-group">
                              <span className="input-group-addon">L</span>
                              <input
                                type="text"
                                className="form-control"
                                value={form.totalVolume || ""}
                                onChange={(e) => inputChange("totalVolume", e.target.value)}
                                placeholder="240"
                              />
                            </div>
                            {renderErrorFeedback(errors.totalVolume)}
                          </div>
                          <div className={`flex-1-1 form-group ${errorClass(errors.totalValue)}`}>
                            <label>{t("expense.form.label.fuelTotalValue")}</label>
                            <div className="input-group">
                              <span className="input-group-addon">R$</span>
                              <input
                                type="text"
                                className="form-control"
                                value={form.totalValue || ""}
                                onChange={(e) => inputChange("totalValue", e.target.value)}
                                placeholder="1.560,00"
                              />
                            </div>
                            {renderErrorFeedback(errors.totalValue)}
                          </div>
                        </div>

                        <div className="display-flex gap-15">
                          <div className="flex-1-1 form-group">
                            <label>{t("expense.form.label.tankCapacity", { unit: fuelUnitLabel })}</label>
                            <input
                              type="text"
                              className="form-control"
                              value={form.tankCapacity || ""}
                              onChange={(e) => inputChange("tankCapacity", e.target.value)}
                              placeholder="300"
                            />
                          </div>
                          <div className="flex-1-1 form-group">
                            <label>{t("expense.form.label.averageConsumption", { unit: consumptionUnitLabel })}</label>
                            <input
                              type="text"
                              className="form-control"
                              value={form.averageConsumption || ""}
                              onChange={(e) => inputChange("averageConsumption", e.target.value)}
                              placeholder="2,5"
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label>{t("expense.form.label.fuelUnitPrice")}</label>
                          <div className="input-group">
                            <span className="input-group-addon">R$</span>
                            <input
                              type="text"
                              className="form-control"
                              value={form.totalUnitPrice || ""}
                              readOnly
                              placeholder="6,50"
                            />
                          </div>
                        </div>

                        <hr className="margin-y-20" />

                        <div className="margin-bottom-20">
                          <div className="text-color-primary text-size-16 text-medium margin-bottom-15">
                            {t("expense.form.label.additionalComplement")}
                          </div>

                          <ComplementList
                            complements={complementList}
                            errors={complementErrors}
                            typeOptions={COMPLEMENT_TYPES}
                            labels={{ ...complementLabels, value: t("expense.form.label.complementTotalValue") }}
                            onChangeType={changeComplementType}
                            onChangeVolume={changeComplementVolume}
                            onChangeValue={changeComplementValue}
                            renderRemoveButton={(index) => (
                              <Button
                                onClick={() => removeComplement(index)}
                                className="btn-link"
                                bsStyle={Button.DANGER}
                                iconName="rioglyph rioglyph-trash"
                                style={{ padding: "0 12px !important" }}
                              >
                                {t("common.action.remove")}
                              </Button>
                            )}
                          />

                          <Button
                            onClick={addComplement}
                            iconName="rioglyph rioglyph-plus-light"
                            className="rounded-small margin-right-5"
                          >
                            {t("expense.form.action.addComplement")}
                          </Button>
                        </div>
                      </>
                    )}

                    {!isHybridSubcategory && (
                      <>
                        <div className={`form-group ${errorClass(errors.volume)}`}>
                          <label htmlFor="volume">{t("expense.form.label.totalVolume")}</label>
                          <div className="input-group">
                            <span className="input-group-addon">{fuelUnitLabel}</span>
                            <input
                              id="volume"
                              type="text"
                              className="form-control"
                              value={form.volume || ""}
                              onChange={(e) => inputChange("volume", e.target.value)}
                              placeholder="240"
                            />
                          </div>
                          {renderErrorIcon(errors.volume)}
                          <span className="help-block">
                            <span>{errors.volume}</span>
                          </span>
                        </div>

                        <div className="display-flex gap-15">
                          <div className="flex-1-1 form-group">
                            <label htmlFor="tankCapacity">
                              {t("expense.form.label.tankCapacity", { unit: fuelUnitLabel })}
                            </label>
                            <input
                              id="tankCapacity"
                              type="text"
                              className="form-control"
                              value={form.tankCapacity || ""}
                              onChange={(e) => inputChange("tankCapacity", e.target.value)}
                              placeholder="300"
                            />
                          </div>
                          <div className="flex-1-1 form-group">
                            <label htmlFor="averageConsumption">
                              {t("expense.form.label.averageConsumption", { unit: consumptionUnitLabel })}
                            </label>
                            <input
                              id="averageConsumption"
                              type="text"
                              className="form-control"
                              value={form.averageConsumption || ""}
                              onChange={(e) => inputChange("averageConsumption", e.target.value)}
                              placeholder="2,5"
                            />
                          </div>
                        </div>

                        <div className={`form-group ${errorClass(errors.value)}`}>
                          <label htmlFor="inputValue">{t("expense.form.label.fuelTotalValue")}</label>
                          <div className="input-group">
                            <span className="input-group-addon">R$</span>
                            <input
                              id="inputValue"
                              type="text"
                              className="form-control"
                              value={form.value}
                              onChange={(e) => inputChange("value", e.target.value)}
                              name="value"
                            />
                          </div>
                          {renderErrorIcon(errors.value)}
                          <span className="help-block">
                            <span>{errors.value}</span>
                          </span>
                        </div>

                        <div className="form-group">
                          <label htmlFor="fuelUnitPrice">{t("expense.form.label.fuelUnitPrice")}</label>
                          <div className="input-group">
                            <span className="input-group-addon">R$</span>
                            <input
                              id="fuelUnitPrice"
                              type="text"
                              className="form-control"
                              value={form.fuelUnitPrice || ""}
                              readOnly
                              placeholder="6,50"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {!isHybridSubcategory && !showComplementForm && (
                      <div className="margin-bottom-15">
                        <Button
                          onClick={() => {
                            setShowComplementForm(true);
                            addComplement();
                          }}
                          iconName="rioglyph rioglyph-plus-light"
                          className="rounded-small margin-right-5"
                        >
                          {t("expense.form.action.addComplement")}
                        </Button>
                      </div>
                    )}

                    {!isHybridSubcategory && showComplementForm && (
                      <div className="margin-bottom-20">
                        <div className="text-color-primary text-size-16 text-medium margin-bottom-15">
                          {t("expense.form.label.additionalComplement")}
                        </div>

                        <ComplementList
                          complements={complementList}
                          errors={complementErrors}
                          typeOptions={COMPLEMENT_TYPES}
                          labels={{ ...complementLabels, value: t("expense.form.label.complementValue") }}
                          onChangeType={changeComplementType}
                          onChangeVolume={changeComplementVolume}
                          onChangeValue={changeComplementValue}
                          renderRemoveButton={(index) => (
                            <button
                              type="button"
                              className="btn btn-link padding-0"
                              onClick={() => removeComplementAndCloseForm(index)}
                            >
                              <span className="rioglyph rioglyph-trash margin-right-5 text-color-danger"></span>
                              <span className="text-color-danger text-medium">{t("common.action.remove")}</span>
                            </button>
                          )}
                        />

                        <Button
                          onClick={addComplement}
                          iconName="rioglyph rioglyph-plus-light"
                          className="rounded-small margin-right-5"
                        >
                          {t("expense.form.action.addComplement")}
                        </Button>
                      </div>
                    )}
                  </>
                );
              })()}

              <hr className="margin-y-20" />

              <InputSelect
                disabled={isEdit && !!branchs.find((b) => b.id === form.branch)?.inactive}
                inputChange={inputChange}
                label={t("expense.form.label.branch")}
                name="branch"
                newOption={newOption}
                onCreateNewOption={onCreateNewOption}
                options={branchs}
                value={form.branch}
                errorMessage={errors.branch}
                onInactiveSelect={(item) => onInactiveSelect?.(item, "branch")}
                inactiveEntityLabel="filial"
                useClear
              />

              <InputSelect
                disabled={isEdit && !!segments.find((s) => s.id === form.segment)?.inactive}
                inputChange={inputChange}
                label={t("expense.form.label.segment")}
                name="segment"
                newOption={newOption}
                onCreateNewOption={onCreateNewOption}
                options={segments}
                value={form.segment}
                errorMessage={errors.segment}
                onInactiveSelect={(item) => onInactiveSelect?.(item, "segment")}
                inactiveEntityLabel="segmento"
                useClear
              />

              <InputSelect
                disabled={isEdit && !!suppliers.find((s) => s.id === form.supplier)?.inactive}
                inputChange={inputChange}
                label={t("expense.form.label.supplier")}
                name="supplier"
                newOption={newOption}
                onCreateNewOption={onCreateNewOption}
                options={suppliers}
                value={form.supplier}
                errorMessage={errors.supplier}
                onInactiveSelect={(item) => onInactiveSelect?.(item, "supplier")}
                inactiveEntityLabel="fornecedor"
                useClear
              />

              <InputSelect
                inputChange={inputChange}
                label={isVehicleRequired ? t("expense.form.label.vehicleRequired") : t("expense.form.label.vehicle")}
                name="vehicle"
                onCreateNewOption={onCreateNewOption}
                options={vehicles}
                value={form.vehicle}
                errorMessage={errors.vehicle}
                disabled={isVehicleLockedByTrip}
                useClear
              />

              {DRIVER_SLOT_IDS.slice(0, Math.max(driversQuantity, 0)).map((slotId, index) => {
                const label = getDriverLabel(index);
                const isLastDriver = index === MAX_DRIVERS - 1;

                return (
                  <InputSelect
                    key={slotId}
                    inputChange={(_, value) =>
                      value === undefined ? inputClearDriver(index) : inputDriverChange(value, index)
                    }
                    label={label}
                    name="driver"
                    onCreateNewOption={onCreateNewOption}
                    options={drivers}
                    value={form.driver[index]}
                    errorMessage={errors.driver}
                    disabled={isDriverLockedByTrip}
                    onRight={
                      !isLastDriver ? () => setDriversQuantity((prev) => Math.min(MAX_DRIVERS, prev + 1)) : undefined
                    }
                    iconRight={!isLastDriver ? "ioglyph rioglyph-plus-light" : undefined}
                    disabledRight={isDriverLockedByTrip || !form?.driver[index] || driversQuantity >= MAX_DRIVERS}
                    useClear
                    tooltip={isLastDriver ? DRIVER_LIMIT_TOOLTIP : undefined}
                  />
                );
              })}

              <InputSelect
                inputChange={(_, value) => handleTripChange(value as string | undefined)}
                onFilterChange={setTripSearch}
                pinNewOption
                onLoadMore={onTripsLoadMore}
                hasMore={hasMoreTrips}
                isLoadingMore={isLoadingMoreTrips}
                label={t("expense.form.label.trip")}
                noItemMessage={t("expense.form.trip.empty")}
                name="trip"
                newOption={newOption}
                onCreateNewOption={onCreateNewOption}
                options={trips}
                value={form.trip}
                errorMessage={errors.trip}
                useClear
              />

              <InputSelect
                inputChange={inputChange}
                label={t("expense.form.label.paymentMethod")}
                name="paymentMethod"
                newOption={newOption}
                onCreateNewOption={onCreateNewOption}
                options={paymentMethodOptions}
                value={form?.paymentMethod as string}
                errorMessage={errors.paymentMethod}
                onInactiveSelect={(item) => onInactiveSelect?.(item, "paymentMethod")}
                inactiveEntityLabel="meio de pagamento"
                useClear
              />

              {(() => {
                const selectedCategory = categories.find((c) => c.id === form.category);
                const isFuelCategory = normalizeText(
                  selectedCategory?.rawName ?? (selectedCategory?.label as string)
                ).includes("abastecimento");

                if (!isFuelCategory) {
                  return (
                    <div className={`form-group ${errorClass(errors.value)}`}>
                      <label htmlFor="inputValueGeneric">{t("expense.form.label.value")}</label>
                      <div className="input-group">
                        <span className="input-group-addon">R$</span>
                        <input
                          id="inputValueGeneric"
                          type="text"
                          className="form-control"
                          value={form.value}
                          onChange={(e) => inputChange("value", e.target.value)}
                          placeholder="0,00"
                        />
                      </div>
                      {renderErrorIcon(errors.value)}
                      <span className="help-block">
                        <span>{errors.value}</span>
                      </span>
                    </div>
                  );
                }

                return null;
              })()}

              <div className="form-group">
                <label htmlFor="inputObservation">{t("expense.form.label.observation")}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  id="inputObservation"
                  value={form.observation}
                  onChange={(e) => inputChange("observation", e.target.value)}
                  name="observation"
                  placeholder="Digite aqui..."
                ></textarea>
              </div>
            </div>
          </fieldset>

          <fieldset>
            <div className="">
              {(() => {
                const selectedCategory = categories.find((c) => c.id === form.category);
                const categoryLabelNorm = normalizeText(
                  selectedCategory?.rawName ?? (selectedCategory?.label as string)
                );
                const isCategoryPrice = categoryLabelNorm.includes("abastecimento");

                if (isCategoryPrice) {
                  return (
                    <div>
                      <div className="form-group margin-top-20">
                        <span>
                          <span className="display-flex align-items-center gap-5">
                            <span className="rioglyph rioglyph-camera"></span>
                            <span className="text-thin">{t("expense.form.label.addOdometerPhoto")}</span>
                          </span>
                        </span>

                        <Button
                          onClick={() => handleButtonClick("ODOMETER")}
                          disabled={isStatusOnlyEdit}
                          iconName="rioglyph rioglyph-upload"
                          className="rounded-small margin-right-5 margin-top-10"
                        >
                          {t("expense.form.action.attachReceipt")}
                        </Button>

                        {fileNameOdometer &&
                          renderAttachmentChip(
                            1,
                            () => fileClear("ODOMETER"),
                            !!(isEdit && transactionId && !hasNewOdometerAttachment),
                            () => transactionId && onDownloadOdometerFile?.(transactionId)
                          )}

                        <input
                          type="file"
                          ref={fileInputOdometerRef}
                          className="hidden"
                          onChange={handleFileOdometerChange}
                        />
                        {errorFileOdometer && (
                          <p className="text-color-danger margin-0 margin-top-5">{errorFileOdometer}</p>
                        )}
                      </div>

                      <div className="form-group margin-top-20">
                        <span>
                          <span className="text-medium">{t("expense.form.label.invoice")}</span>
                          <span className="display-flex align-items-center gap-5">
                            <span className="rioglyph rioglyph-camera"></span>
                            <span className="text-thin">{t("expense.form.label.selectFromGallery")}</span>
                          </span>
                        </span>

                        <Button
                          onClick={() => handleButtonClick("DEFAULT")}
                          disabled={isStatusOnlyEdit}
                          iconName="rioglyph rioglyph-upload"
                          className="rounded-small margin-right-5"
                        >
                          {t("expense.form.action.attachReceipt")}
                        </Button>
                        {fileName &&
                          renderAttachmentChip(
                            2,
                            () => fileClear("DEFAULT"),
                            !!(isEdit && transactionId && !hasNewAttachment),
                            () => transactionId && onDownloadFile?.(transactionId)
                          )}
                        <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} />
                        {errorFile && <p className="text-color-danger margin-0 margin-top-5">{errorFile}</p>}
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="form-group margin-top-20">
                    <Button
                      onClick={() => handleButtonClick("DEFAULT")}
                      disabled={isStatusOnlyEdit}
                      iconName="rioglyph rioglyph-upload"
                      className="rounded-small margin-right-5"
                    >
                      {t("expense.form.action.attachReceipt")}
                    </Button>
                    {fileName &&
                      renderAttachmentChip(
                        1,
                        () => fileClear("DEFAULT"),
                        !!(isEdit && transactionId && !hasNewAttachment),
                        () => transactionId && onDownloadFile?.(transactionId)
                      )}
                    <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} />
                    {errorFile && <p className="text-color-danger margin-0 margin-top-5">{errorFile}</p>}
                  </div>
                );
              })()}
            </div>
          </fieldset>
        </form>
      </div>
      <div className="display-flex flex-wrap gap-10 justify-content-between align-items-center border border-top-only border-width-1 border-color-lighter padding-20">
        <Button onClick={() => openFormModal(false)} className="width-150 rounded-small width-100pct-xs">
          {t("common.action.cancel")}
        </Button>
        <div className="display-flex flex-wrap gap-10 width-100pct-xs">
          {isEdit ? null : (
            <Button
              onClick={handleSubmitAndCreateNew}
              disabled={isLoadingSubmit}
              className="width-150 rounded-small width-100pct-xs"
            >
              {t("expense.form.action.saveAndCreateNew")}
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            bsStyle={Button.PRIMARY}
            disabled={isLoadingSubmit}
            className="width-150 rounded-small width-100pct-xs"
            iconName={isLoadingSubmit ? "rioglyph rioglyph-refresh spinning" : undefined}
          >
            {t("common.action.save")}
          </Button>
        </div>
      </div>
    </>
  );
};

export default ExpenseForm;
